import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useGet, usePost, usePut } from "../../../common/api-service";
import useUsersStore from "./store";
import useFacilitiesDropdownStore from "../../../common/components/dropdowns/facilities-dropdown/api/store";
import useProductDropdownStore from "../../../common/components/dropdowns/products-dropdown/api/store";
import { FacilityModel } from "../../facilities/api/types";
import { UsersSchema } from "./schema";
import { signFile } from "../../../common/utils/file-signing";
import {
  ChangeUserPasswordModel,
  CreatedUserResponseModel,
  UserFPRModel,
  WalkthroughModel,
} from "./types";
import queryClient from "../../../common/query-client";
import { PagedUserModel } from "../../users-facility/api/types";
import { signString } from "../../../common/utils/string-signing";
import { useGetSpecificRole } from "../../../common/api/roles/hooks";
import { getRoleNumber, UserRoles } from "../../../common/enums/user-roles";
import { UserFormSchema } from "../../../common/components/user-inv-modal/api/schema";
import useMainStore from "../../landing-page/api/store";
import { EmailResendModule } from "../../../common/enums/email-module";

export const useGetPagedUsersByFacility = (disabled?: boolean) => {
  const { get } = useGet();
  const { pageNumber, pageSize, searchText } = useUsersStore();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { selected: selectedProduct } = useProductDropdownStore();

  const isFacilitySelected = !!selectedFacility?.id;
  const isProductSelected = !!selectedProduct?.id;

  const url =
    `/api/users/facility-user-UFP?PageNumber=${pageNumber}&PageSize=${pageSize}&IsInternal=true` +
    (selectedFacility
      ? `&FacilityId=${encodeURIComponent(selectedFacility.id)}`
      : "") +
    (selectedProduct
      ? `&ProductId=${encodeURIComponent(selectedProduct.id!)}`
      : "") +
    (searchText ? `&SearchText=${encodeURIComponent(searchText)}` : "");
  return useQuery<PagedUserModel>({
    queryFn: () => get<PagedUserModel>(url),
    queryKey: [
      "users-by-facility-and-product",
      {
        pageNumber,
        pageSize,
        productId: selectedProduct?.id,
        facilityId: selectedFacility?.id,
        searchText: searchText,
      },
    ],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
    enabled: isFacilitySelected && isProductSelected && !disabled,
  });
};

export const useGetFacilitiesWithProducts = (
  options?: UseQueryOptions<FacilityModel[], Error>
) => {
  const { get } = useGet();
  const { selected } = useProductDropdownStore();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { activeUser, setSelectedFacilities, setSelectedProducts, setRole } =
    useUsersStore();
  const { selectedClient } = useMainStore();

  const url = `/api/facility/by-client-product?ProductId=${encodeURIComponent(
    selected?.id ?? ""
  )}&UserId=${encodeURIComponent(
    activeUser?.id ?? ""
  )}&ClientId=${encodeURIComponent(
    selectedClient?.id ?? ""
  )}&FacilityId=${encodeURIComponent(selectedFacility?.id ?? "")}`;

  return useQuery<FacilityModel[], Error>({
    queryKey: [
      "facilities-with-products",
      encodeURIComponent(selected?.productCode ?? ""),
      selectedFacility?.id ?? "",
      activeUser?.id ?? "",
      selectedClient?.id ?? "",
    ],
    queryFn: async () => {
      const data = await get<FacilityModel[]>(url);

      const relevantFacilities = data.filter(
        (f) =>
          f.products &&
          f.products.some((p) => p.role !== undefined && p.role !== null)
      );
      const selectedProductsFlat = relevantFacilities.flatMap(
        (f) =>
          f.products?.filter((p) => p.role !== undefined && p.role !== null) ||
          []
      );

      relevantFacilities.forEach(setSelectedFacilities);
      selectedProductsFlat.forEach((product) => {
        setSelectedProducts(product);
        setRole({
          role: getRoleNumber(product.role ?? UserRoles.None),
          product,
        });
      });

      if (selectedFacility?.id) {
        data.sort((a, b) =>
          a.id === selectedFacility.id
            ? -1
            : b.id === selectedFacility.id
            ? 1
            : 0
        );
      }

      return data;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

export const useGetUserFacilityProductById = () => {
  const { get } = useGet();
  const { activeUser, isEditModal, setSelectedUser, setIsAddModal } =
    useUsersStore();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { selected: selectedProduct } = useProductDropdownStore();

  const getSpecificRole = useGetSpecificRole(UserRoles.User);

  var url = `/api/users/get-user-facility-product-by-id?UserId=${encodeURIComponent(
    activeUser?.id || ""
  )}`;
  if (selectedFacility?.id)
    url += `&FacilityId=${encodeURIComponent(selectedFacility.id)}`;
  if (selectedProduct?.id)
    url += `&ProductId=${encodeURIComponent(selectedProduct.id)}`;

  return useQuery<UserFPRModel>({
    queryFn: async () => {
      const result = await get<UserFPRModel>(url);

      getSpecificRole.mutate("User", {
        onSuccess: (role) => {
          const user: UserFormSchema = {
            id: result.id,
            prefix: result.prefix,
            lastName: result.lastName,
            firstName: result.firstName,
            middleName: result.middleName,
            suffix: result.suffix,
            customName: result.customName,
            email: result.email,
            civilStatus: result.civilStatus,
            gender: result.gender,
            department: result.department ?? undefined,
            birthDate: result.birthDate || "",
            photoUrl: result.photoUrl,

            role: {
              id: role?.id ?? "",
              name: role?.name ?? "",
              displayName: role?.displayName ?? "",
              role: role?.name ?? "",
            },
          };
          setSelectedUser(user);
          setIsAddModal(true);
        },
      });

      return result;
    },
    queryKey: [
      "get-user-facility-product-by-id",
      encodeURIComponent(activeUser?.id || ""),
      encodeURIComponent(selectedFacility?.id || ""),
      encodeURIComponent(selectedProduct?.id || ""),
    ],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
    enabled: isEditModal && activeUser !== undefined,
  });
};

export const useCreateUser = () => {
  const { post } = usePost();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { selected: selectedProduct } = useProductDropdownStore();
  const { pageNumber, pageSize, searchText } = useUsersStore();
  const mainStore = useMainStore();

  return useMutation({
    mutationFn: async (data: UsersSchema) => {
      const formData = new FormData();
      //formData.append("Products", JSON.stringify(Object.fromEntries(data.products.entries())));
      //formData.append("User", JSON.stringify(data.user));
      formData.append("ClientId", mainStore.selectedClient?.id ?? "");
      formData.append("ProductId", selectedProduct?.id ?? "");
      formData.append("FacilityId", selectedFacility?.id ?? "");

      data.products.forEach((product, index) => {
        formData.append(`Products[${index}][id]`, product.id);
        formData.append(`Products[${index}][facilityId]`, product.facilityId);
        //formData.append(`Products[${index}][productFacilityId]`, product.productFacilityId);
        formData.append(`Products[${index}][role]`, product.role.toString());
        formData.append(
          `Products[${index}][clientProductFacilityId]`,
          product.clientProductFacilityId
        );
      });

      // Append individual user fields (excluding photo as it's handled separately)
      formData.append("User.Email", data.user.email);
      formData.append("User.FirstName", data.user.firstName);
      formData.append("User.LastName", data.user.lastName);
      if (data.user.birthDate)
        formData.append("User.BirthDate", data.user.birthDate);
      formData.append("User.Role.Role", data.user.role.role.toString());
      formData.append("User.Role.Id", data.user.role.id);
      if (data.user.id) formData.append("User.Id", data.user.id);
      if (data.user.civilStatus !== undefined && data.user.civilStatus !== null)
        formData.append("User.CivilStatus", data.user.civilStatus.toString());
      if (data.user.gender !== undefined && data.user.gender !== null)
        formData.append("User.Gender", data.user.gender.toString());
      if (data.user.department !== undefined && data.user.department !== null)
        formData.append("User.Department", data.user.department.toString());
      if (data.user.productCode)
        formData.append("User.ProductCode", data.user.productCode);
      if (data.user.facilityCode)
        formData.append("User.FacilityCode", data.user.facilityCode);
      if (data.user.prefix) formData.append("User.Prefix", data.user.prefix);
      if (data.user.suffix) formData.append("User.Suffix", data.user.suffix);
      if (data.user.customName)
        formData.append("User.CustomName", data.user.customName);
      if (data.user.middleName)
        formData.append("User.MiddleName", data.user.middleName);

      if (data.user.photoUrl instanceof File) {
        const signature = await signFile(data.user.photoUrl);
        formData.append("UserPhoto", data.user.photoUrl);
        formData.append("UserPhotoSignature", signature);
      }

      return post<FormData, CreatedUserResponseModel>(
        `/api/users/facility-user-UFP`,
        formData,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },
    onSuccess: (data: CreatedUserResponseModel) => {
      queryClient.invalidateQueries({
        queryKey: [
          "users-by-facility-and-product",
          {
            pageNumber,
            pageSize,
            productId: selectedProduct?.id,
            facilityId: selectedFacility?.id,
            searchText: searchText,
          },
        ],
      });
      return data;
    },
  });
};

export const useChangeUserPassword = () => {
  const { put } = usePut();

  return useMutation({
    mutationFn: async (data: ChangeUserPasswordModel) => {
      data.passwordSignature = await signString(data.newPassword);
      return put<ChangeUserPasswordModel, string>(
        `/api/users/change-user-password`,
        data
      );
    },
  });
};

export const useLockUser = () => {
  const { put } = usePut();
  const { activeUser } = useUsersStore();
  const { pageNumber, pageSize, searchText } = useUsersStore();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { selected: selectedProduct } = useProductDropdownStore();

  return useMutation({
    mutationFn: async (data: string) => {
      return put<object, string>(`/api/users/lock-user`, {
        userId: activeUser?.id,
        lockReason: data,
        facilityId: selectedFacility?.id,
        productId: selectedProduct?.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "users-by-facility-and-product",
          {
            pageNumber,
            pageSize,
            productId: selectedProduct?.id,
            facilityId: selectedFacility?.id,
            searchText: searchText,
          },
        ],
      });
    },
  });
};

export const useRemoveUser = () => {
  const { put } = usePut();
  const { activeUser } = useUsersStore();
  const { pageNumber, pageSize, searchText } = useUsersStore();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { selected: selectedProduct } = useProductDropdownStore();

  return useMutation({
    mutationFn: async (data: string) => {
      return put<object, string>(`/api/users/remove-user-from-facility`, {
        userId: activeUser?.id,
        facilityId: selectedFacility?.id,
        productId: selectedProduct?.id,
        roleId: activeUser?.role?.id,
        reason: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "users-by-facility-and-product",
          {
            pageNumber,
            pageSize,
            productId: selectedProduct?.id,
            facilityId: selectedFacility?.id,
            searchText: searchText,
          },
        ],
      });
    },
  });
};

export const useUpdateUser = () => {
  const { put } = usePut();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { selected: selectedProduct } = useProductDropdownStore();
  const { selectedClient } = useMainStore();
  const { pageNumber, pageSize, searchText } = useUsersStore();

  return useMutation({
    mutationFn: async (data: UsersSchema) => {
      const formData = new FormData();
      //formData.append("Products", JSON.stringify(Object.fromEntries(data.products.entries())));
      //formData.append("User", JSON.stringify(data.user));
      data.products.forEach((product, index) => {
        formData.append(`Products[${index}][id]`, product.id);
        formData.append(`Products[${index}][facilityId]`, product.facilityId);
        //  formData.append(`Products[${index}][productFacilityId]`, product.productFacilityId);
        formData.append(`Products[${index}][role]`, product.role.toString());
        formData.append(
          `Products[${index}][clientProductFacilityId]`,
          product.clientProductFacilityId
        );
      });

      // Append individual user fields (excluding photo as it's handled separately)
      formData.append("User.Email", data.user.email);
      formData.append("User.FirstName", data.user.firstName);
      formData.append("User.LastName", data.user.lastName);
      if (data.user.birthDate)
        formData.append("User.BirthDate", data.user.birthDate);
      formData.append("User.Role.Role", data.user.role.role.toString());
      formData.append("User.Role.Id", data.user.role.id);
      if (data.user.id) formData.append("User.Id", data.user.id);
      if (data.user.civilStatus !== undefined && data.user.civilStatus !== null)
        formData.append("User.CivilStatus", data.user.civilStatus.toString());
      if (data.user.gender !== undefined && data.user.gender !== null)
        formData.append("User.Gender", data.user.gender.toString());
      if (data.user.department !== undefined && data.user.department !== null)
        formData.append("User.Department", data.user.department.toString());
      if (data.user.productCode)
        formData.append("User.ProductCode", data.user.productCode);
      if (data.user.facilityCode)
        formData.append("User.FacilityCode", data.user.facilityCode);
      if (data.user.prefix) formData.append("User.Prefix", data.user.prefix);
      if (data.user.suffix) formData.append("User.Suffix", data.user.suffix);
      if (data.user.customName)
        formData.append("User.CustomName", data.user.customName);
      if (data.user.middleName)
        formData.append("User.MiddleName", data.user.middleName);

      if (data.user.photoUrl instanceof File) {
        const signature = await signFile(data.user.photoUrl);
        formData.append("UserPhoto", data.user.photoUrl);
        formData.append("UserPhotoSignature", signature);
      }

      formData.append("ClientId", selectedClient?.id ?? "");
      formData.append("ProductId", selectedProduct?.id ?? "");
      formData.append("FacilityId", selectedFacility?.id ?? "");

      return put<FormData, CreatedUserResponseModel>(
        `/api/users/facility-user-UFP`,
        formData,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },
    onSuccess: (data: CreatedUserResponseModel) => {
      queryClient.invalidateQueries({
        queryKey: [
          "users-by-facility-and-product",
          {
            pageNumber,
            pageSize,
            productId: selectedProduct?.id,
            facilityId: selectedFacility?.id,
            searchText: searchText,
          },
        ],
      });
      return data;
    },
  });
};

export interface ConfirmPasswordModel {
  oldPassword: string;
  userId: string;
}

export const useResetPassword = () => {
  const { post } = usePost();
  const { pageNumber, pageSize, searchText } = useUsersStore();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { selected: selectedProduct } = useProductDropdownStore();
  const { selectedClient } = useMainStore();
  return useMutation({
    mutationFn: async (data: ConfirmPasswordModel) => {
      const request = {
        ...data,
        clientId: selectedClient?.id ?? "",
        facilityId: selectedFacility?.id ?? "",
        productId: selectedProduct?.id ?? "",
      };

      return post(`/api/users/reset-password`, request, false, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "users-by-facility-and-product",
          {
            pageNumber,
            pageSize,
            productId: selectedProduct?.id,
            facilityId: selectedFacility?.id,
            searchText: searchText,
          },
        ],
      });
    },
  });
};

export type ResendEmailModel = {
  userId: string;
};

export const useResendEmail = (module: EmailResendModule) => {
  const { put } = usePut();
  const { selectedFacility } = useFacilitiesDropdownStore();
  const { selected: selectedProduct } = useProductDropdownStore();
  const { selectedClient } = useMainStore();

  return useMutation({
    mutationFn: async (data: ResendEmailModel) => {
      const request = {
        ...data,
        clientId: selectedClient?.id ?? "",
        facilityId: selectedFacility?.id ?? "",
        productId: selectedProduct?.id ?? "",
        emailResendModule: module,
      };

      return put<ResendEmailModel, string>(
        `/api/users/resend-email`,
        request,
        false
      );
    },
  });
};

export const useGetUserWalkthroughTrack = () => {
  const { get } = useGet();
  var url = "/api/user-walkthrough";

  return useQuery<WalkthroughModel>({
    queryFn: () => get<WalkthroughModel>(url),
    queryKey: ["walkthrough-tracker"],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
  });
};
interface WalkthroughPayload {
  UserId: string;
  IsFacilityUsersFinished: boolean;
}

export const useUpdateUserWalkthrough = () => {
  const { put } = usePut();

  return useMutation<string, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const payload: WalkthroughPayload = {
        UserId: userId,
        IsFacilityUsersFinished: true,
      };
      return put<WalkthroughPayload, string>(
        `/api/user-walkthrough`,
        payload,
        false
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["walkthrough-tracker"] });
    },
  });
};
